import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    ArrowRight,
    BadgePercent,
    type LucideIcon,
    Megaphone,
    Package,
    Sparkles,
} from 'lucide-react';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { extractArray } from '@/lib/shared-utils';
import { cn } from '@/lib/utils';
import {
    fetchAnnouncements,
    fetchBillboard,
} from '@/pages/Announcements/api';
import type {
    Announcement,
    AnnouncementType,
    BillboardAnnouncement,
} from '@/pages/Announcements/types';

function getDaysLeft(toTime: string): string {
    const diff = new Date(toTime).getTime() - Date.now();
    const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    if (days === 0) return 'Expires today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
}

interface TypeMeta {
    label: string;
    badge: string;
    tile: string;
    icon: LucideIcon;
}

const TYPE_META: Record<AnnouncementType, TypeMeta> = {
    general: {
        label: 'General',
        badge: 'bg-muted text-muted-foreground',
        tile: 'bg-muted text-muted-foreground',
        icon: Megaphone,
    },
    special: {
        label: 'Special',
        badge: 'bg-primary/10 text-primary',
        tile: 'bg-primary/10 text-primary',
        icon: Sparkles,
    },
    new_product_alert: {
        label: 'Product',
        badge: 'bg-warning/10 text-warning',
        tile: 'bg-warning/10 text-warning',
        icon: Package,
    },
    scheme_alert: {
        label: 'Scheme',
        badge: 'bg-success/10 text-success',
        tile: 'bg-success/10 text-success',
        icon: BadgePercent,
    },
};

interface OfferItem {
    id: string;
    title: string;
    type: AnnouncementType;
    subtitle: string;
}

function OfferRow({ item, onClick }: { item: OfferItem; onClick: () => void }) {
    const meta = TYPE_META[item.type] ?? TYPE_META.general;
    const Icon = meta.icon;
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full items-center gap-3 border-b border-border/40 px-2 py-3 text-left transition-colors hover:bg-muted/40"
        >
            <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', meta.tile)}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">{item.title}</p>
                <p className="truncate text-xs text-muted-foreground">{item.subtitle}</p>
            </div>
            <span className={cn('shrink-0 rounded-full px-3 py-1 text-xs font-semibold', meta.badge)}>
                {meta.label}
            </span>
        </button>
    );
}

export function SchemesOffersCard() {
    const navigate = useNavigate();

    const { data: billboardRes, isLoading: lb } = useQuery({
        queryKey: ['billboard'],
        queryFn: () => fetchBillboard({ page_size: 6 }),
    });

    const { data: ownRes, isLoading: lo } = useQuery({
        queryKey: ['announcements', 'own'],
        queryFn: () => fetchAnnouncements({ page_size: 6 }),
    });

    const billboard: BillboardAnnouncement[] = extractArray(billboardRes?.data);
    const own: Announcement[] = extractArray(ownRes?.data);
    const isLoading = lb || lo;

    const items: OfferItem[] = [
        ...billboard.map((b) => ({
            id: `b-${b.id}`,
            title: b.title,
            type: b.type,
            subtitle: `${b.owner_business_name || b.owner_username || 'Partner'} · ${getDaysLeft(b.to_time)}`,
        })),
        ...own.map((a) => ({
            id: `a-${a.id}`,
            title: a.title,
            type: a.type,
            subtitle: `${a.description?.trim() || 'Your announcement'} · ${getDaysLeft(a.to_time)}`,
        })),
    ].slice(0, 5);

    const goToAnnouncements = () => navigate('/announcements');

    return (
        <Card className="lg:col-span-2 gap-0 rounded-xl border-border/70 py-0 shadow-xs">
            <CardHeader className="flex! items-start justify-between gap-2 px-5 pt-5 pb-3">
                <div className="space-y-1">
                    <CardTitle className="text-base font-semibold">
                        Schemes & Offers
                    </CardTitle>
                    <CardDescription className="text-xs">
                        Active campaigns running now.
                    </CardDescription>
                </div>
                {items.length > 0 && (
                    <span className="mt-0.5 shrink-0 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                        {`${items.length} active`}
                    </span>
                )}
            </CardHeader>
            <CardContent className="flex flex-col px-3 pb-2">
                {isLoading && (
                    <div className="flex justify-center py-10">
                        <Spinner className="h-4 w-4 text-muted-foreground" />
                    </div>
                )}

                {!isLoading && items.length === 0 && (
                    <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                            <Megaphone className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium text-foreground">No active offers yet</p>
                        <p className="text-xs text-muted-foreground">
                            Schemes from you and your partners will show up here.
                        </p>
                    </div>
                )}

                {!isLoading && items.map((item) => (
                    <OfferRow key={item.id} item={item} onClick={goToAnnouncements} />
                ))}

                {!isLoading && items.length > 0 && (
                    <div className="px-2 pt-3.5">
                        <button
                            type="button"
                            className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            onClick={goToAnnouncements}
                        >
                            View all offers
                            <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
